import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { FieldValues, useForm } from 'react-hook-form';
import { AxiosResponse } from 'axios';
import Select, { SingleValue } from 'react-select';

import './payment.scss';
import PlainNavbar from 'components/plainNavbar/plainNavbar';
import { ReactComponent as CheckTickIcon } from 'assets/icons/check_circle.svg';
import { ReactComponent as ActiveIcon } from 'assets/icons/active_icon.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowdown.svg';
import Button from 'common/button/button';
import PopupFrame from 'layouts/PopupFrame';
import useLoadingStore from 'zustands/useLoadingStore';
import useErrorLog from 'hooks/useErrorLog';
import { useSubscribeCustomers, useSubscribeOrders, useSubscribePlans, useSubscribeStatus } from 'hooks/useSubscribe';
import { IOption } from 'types/common';
import { addComma } from 'constants/utils';
import priceArrow from 'assets/images/price_arrow.png';
import useApi from 'hooks/useApi';
import CommonModal from 'common/CommonModal';
import { cK } from '@fullcalendar/core/internal-common';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';

const monthOptions = [
  { value: '1', label: '1개월' },
  { value: '6', label: '6개월' },
  { value: '12', label: '12개월' },
];

const Payment: React.FC = () => {
  const navigate = useNavigate();

  const { setLoadingState } = useLoadingStore();

  const { checkErrorLog } = useErrorLog();
  const { webApiNode } = useApi();
  const { getSubscribePlans, products } = useSubscribePlans();
  const { applySubscribeCustomers, customerData } = useSubscribeCustomers();
  const { applySubscribeOrders, orderData } = useSubscribeOrders();
  const { getSubscribeStatus, status } = useSubscribeStatus();
  const { setModalState } = useAlertConfirmStore();

  const [selectItemBasic, setSelectItemBasic] = React.useState<IOption>({ value: '1', label: '1개월' });
  const [selectItemBusiness, setSelectItemBusiness] = React.useState<IOption>({ value: '1', label: '1개월' });
  const [emailApplyModalShow, setEmailApplyModalShow] = React.useState<boolean>(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm(); // 이메일 입력 폼
  const [clickedItem, setClickedItem] = React.useState({
    productCode: '',
    priceCode: '',
  });

  // 구독관련 데이터 패칭
  const fetchData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      await Promise.all([getSubscribePlans(), getSubscribeStatus()]).then((result) => {
        if (result.every((value) => value === true)) {
          setLoadingState({ isFetchLoading: false });
        }
      });
    } catch (err) {
      checkErrorLog(err, ' ');
      setLoadingState({ isFetchLoading: false });
    } finally {
    }
  };

  // 베이직 개월수 셀렉트 핸들러
  const handleBasicMonthHandler = (newValue: SingleValue<IOption>) => {
    if (newValue) setSelectItemBasic(newValue);
  };

  // 비즈니스 개월수 셀렉트 핸들러
  const handleBusinessMonthHandler = (newValue: SingleValue<IOption>) => {
    if (newValue) setSelectItemBusiness(newValue);
  };

  // 결제하기 클릭 - 이메일 체크
  const checkEmailData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getemail`);
      console.log('/auth/getemail_data', data);

      if (data.resultCode === 0) {
        if (data.resultData.EMAIL) {
          applySubscribeCustomers();
        } else {
          setLoadingState({ isFetchLoading: false });
          setEmailApplyModalShow(true);
        }
      } else {
        setLoadingState({ isFetchLoading: false });
        throw new Error(data.resultMsg);
      }
      return true;
    } catch (err) {
      checkErrorLog(err, ' /auth/getemail');
    } finally {
    }
  };

  // 이메일 입력 모달 닫기
  const closeEmailApplyModal = React.useCallback(() => {
    setEmailApplyModalShow(false);
  }, [emailApplyModalShow]);

  // 이메일 입력 완료
  const onSubmitEmail = async (value: FieldValues) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/setemail`, {
        EMAIL: value.email,
      });
      console.log('/auth/setemail_data', data);

      if (data.resultCode === 0) {
        applySubscribeCustomers();
      } else {
        setModalState({ isModalShow: true, title: '이메일 입력', desc: data.resultMsg });
        setLoadingState({ isFetchLoading: false });
        return;
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/setemail');
      setLoadingState({ isFetchLoading: false });
    } finally {
    }
  };

  // 결제하기 클릭 - order생성
  const subscribeOrderHandler = async () => {
    if (!customerData?.ps_sub_code || !customerData?.ps_sub_id || !clickedItem.priceCode || !clickedItem.productCode)
      return;
    const params = {
      customerCode: customerData.ps_sub_code,
      customerId: customerData.ps_sub_id,
      productCode: clickedItem.productCode,
      priceCode: clickedItem.priceCode,
    };
    await applySubscribeOrders(params);
  };

  // 결제하기 화면 이동
  const enterPayScren = () => {
    if (!orderData) return;
    window.location.href = `https://api.steppay.kr/api/public/orders/${orderData.orderCode}/pay?successUrl=${orderData.successUrl}&errorUrl=${orderData.errorUrl}&cancelUrl=${orderData.cancelUrl}`;
  };

  const colourStyles = {
    control: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => ({
      ...styles,
      backgroundColor: 'none',
      border: 'none',
      width: '100%',
      height: '40px',
    }),
    multiValueRemove: (base: any) => ({ ...base, display: 'none' }),
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  React.useEffect(() => {
    subscribeOrderHandler();
  }, [customerData]);

  React.useEffect(() => {
    enterPayScren();
  }, [orderData]);

  return (
    <>
      <PopupFrame>
        <div className="payment">
          <div className="payment__head">
            <div className="payment__head__heading">요금제 선택</div>
            <div className="payment__head__subheading">우리 회사에 맞는 플랜을 선택해보세요</div>
          </div>
          <div className="payment__body">
            {products.length > 0 &&
              products?.map((i, _i) => {
                const selectedMonth = i?.pp_name.includes('베이직') ? selectItemBasic : selectItemBusiness;
                const formatSelectedItem = i.pp_plan_list.find((j) => j.ppp_month === Number(selectedMonth.value));
                return (
                  <div className="payment__body__col" key={i.pp_no}>
                    <div className="payment__body__card">
                      <div className="payment__body__card-head">
                        <div className="payment__body__card-head__package-name">{i.pp_name}</div>
                        {i.pp_name === '무료' ? (
                          <>
                            <div className="payment__body__card-head__section">
                              <div className="payment__body__card-head__package-text">{i.pp_summary[0]}</div>
                              <div className="payment__body__card-head__package-text">{i.pp_summary[1]}</div>
                            </div>
                            <div className="payment__body__card-head__section">
                              <div className="payment__body__card-head__package-cost">Free</div>
                            </div>
                            <div className="payment__body__card-head__section">
                              {status[0]?.pp_name === i.pp_name ? (
                                <div className="payment__body__card-head__package-use">
                                  <ActiveIcon />
                                  현재 사용중
                                </div>
                              ) : (
                                <div className="payment__body__card-head__package-button"></div>
                              )}
                            </div>
                          </>
                        ) : i.pp_name === '프리미엄' ? (
                          <>
                            <div className="payment__body__card-head__section">
                              <div className="payment__body__card-head__package-text2">
                                커스터마이징/ 모바일앱/관리자PC
                              </div>
                              <div className="payment__body__card-head__package-text3">
                                <div>{i.pp_summary[1]}</div>
                                <div>{i.pp_summary[2]}</div>
                              </div>
                            </div>
                            <div className="payment__body__card-head__section">
                              <div className="payment__body__card-head__package-button" style={{ marginTop: 40 }}>
                                <Button
                                  label="도입문의"
                                  type="type15"
                                  action={() => {
                                    window.open('https://bit.ly/3E2fXjO', '_blank');
                                  }}
                                />
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="payment__body__card-head__section">
                              <div className="payment__body__card-head__package-dropdown">
                                <div className="payment__body__card-head__package-text">{i.pp_summary[0]}</div>
                                <Select
                                  className="payment__body__card-head__package-dropdown__item"
                                  components={{
                                    IndicatorSeparator: () => null,
                                    DropdownIndicator: () => (
                                      <div style={{ paddingRight: '15px' }}>
                                        <ArrowDownIcon />
                                      </div>
                                    ),
                                  }}
                                  isSearchable={false}
                                  value={i.pp_name.includes('베이직') ? selectItemBasic : selectItemBusiness}
                                  styles={colourStyles}
                                  options={monthOptions}
                                  onChange={
                                    i.pp_name.includes('베이직') ? handleBasicMonthHandler : handleBusinessMonthHandler
                                  }
                                />
                              </div>
                            </div>
                            <div className="payment__body__card-head__section">
                              {formatSelectedItem?.ppp_discount_rate === 0 ? (
                                <div className="payment__body__card-head__package-cost">
                                  {addComma(formatSelectedItem?.ppp_origin_price.toString() ?? '0')}원
                                </div>
                              ) : (
                                <>
                                  <div className="payment__body__card-head__package-cost">
                                    <div className="payment__body__card-head__package-cost-img">
                                      <img src={priceArrow} alt="가격인하 아이콘" width={120} />
                                    </div>
                                    <div className="payment__body__card-head__package-discount">
                                      <span>{formatSelectedItem?.ppp_discount_rate}% 할인</span>
                                    </div>
                                    <div className="payment__body__card-head__package-cost__price">
                                      {addComma(formatSelectedItem?.ppp_origin_price.toString() ?? '0')}원
                                    </div>
                                  </div>
                                  <div className="payment__body__card-head__package-cost" style={{ marginTop: 15 }}>
                                    {addComma(formatSelectedItem?.ppp_discount_price.toString() ?? '0')}원
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="payment__body__card-head__section">
                              {status[0]?.pp_name === i?.pp_name ? (
                                <div className="payment__body__card-head__package-use">
                                  <ActiveIcon />
                                  현재 구독중
                                </div>
                              ) : (
                                <div className="payment__body__card-head__package-button">
                                  {status[0]?.rss_status !== 'ACTIVE' && (
                                    <Button
                                      label="결제하기"
                                      type="type2"
                                      action={() => {
                                        if (status[0]?.rss_status === 'CANCEL_WAIT') {
                                          setModalState({
                                            isModalShow: true,
                                            title: '비즈니스 결제',
                                            desc: `베이직 구독 취소가 완료되지 않은 경우 고객센터로 문의하여 환불을 진행해 주세요. 환불처리가 완료되면 결제하실 수 있습니다.`,
                                          });
                                        } else {
                                          setClickedItem({
                                            productCode: i.pp_code,
                                            priceCode: formatSelectedItem?.ppp_code ?? '',
                                          });
                                          checkEmailData();
                                        }
                                      }}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      <div className="payment__body__card-body">
                        <div dangerouslySetInnerHTML={{ __html: i.pp_description }} />
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </PopupFrame>
      <CommonModal isVisible={emailApplyModalShow} onClose={closeEmailApplyModal} title="이메일 입력">
        <div className="emailApply">
          <div className="desc">
            <span>구독 서비스를 위한 이메일 입력</span>
          </div>
          <div className="form">
            <form onSubmit={handleSubmit(onSubmitEmail)}>
              <div className="inputArea">
                <input
                  className="emailInput"
                  type="text"
                  placeholder="이메일 입력"
                  {...register('email', { required: true, pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i })}
                />
                <input type="submit" className="submitInput" value={'완료'} />
              </div>
              {errors.email && (
                <div className="errorArea">
                  <span>올바른 이메일 형식을 입력해 주세요</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </CommonModal>
    </>
  );
};
export default Payment;
